// Pure logic behind "Recover from aid" on the Fee Slip / Invoice Payment screen.
//
// A firm (VALS, LACW) sometimes pays an outside invoice - counsel, an interpreter, a report
// writer - out of its own budget before aid is in place, then applies for retrospective aid
// to get the money back. This tracks each such invoice through to recovery.
//
// Everything lives in one shared vals_drafts record per brand, the same way the "VALS to be
// reimbursed" ticks do. The invoices on this screen come from four different back ends, and
// adding stages to each of them would mean four schema changes; a record keyed by invoice id
// behaves the same for all of them. The record holds two things:
//   - `entries`: the recovery stage of an invoice already on the screen, keyed by its row id,
//     with a snapshot of who/what/how much so it still lists if that row is not loaded.
//   - `invoices`: invoices uploaded straight into this tracker, which exist nowhere else.

import { readInvoiceAmount } from "./invoiceAmount.js";

export const RECOUP_FORM_TYPE = "budget_recoup";

// Fixed per brand so every user reads and writes the same record.
const RECOUP_DRAFT_IDS = {
  vals: "00000000-0000-4000-8000-00000000c2a0",
  lacw: "00000000-0000-4000-8000-00000000c2a1",
  rga: "00000000-0000-4000-8000-00000000c2a2",
  eas: "00000000-0000-4000-8000-00000000c2a3",
  admin: "00000000-0000-4000-8000-00000000c2a4",
};
export const recoupDraftId = (brand) => RECOUP_DRAFT_IDS[brand] || RECOUP_DRAFT_IDS.lacw;

export const RECOUP_STAGE_VALUES = ["paid_from_budget", "retro_aid_required", "aid_applied", "recovered"];

export function recoupStages(prefix = "") {
  const firm = String(prefix || "").trim();
  return [
    { value: "paid_from_budget", label: firm ? `Paid from ${firm} budget` : "Paid from budget" },
    { value: "retro_aid_required", label: "Retrospective aid required" },
    { value: "aid_applied", label: "Aid applied (pending)" },
    { value: "recovered", label: "Recovered" },
  ];
}

export const recoupStageLabel = (stage, prefix = "") =>
  recoupStages(prefix).find((option) => option.value === stage)?.label || "";

const text = (value, max = 300) => String(value ?? "").trim().slice(0, max);

export function parseRecoupAmount(value) {
  const amount = Number.parseFloat(String(value ?? "").replace(/[$,\s]/g, ""));
  return Number.isFinite(amount) && amount >= 0 && amount < 10000000 ? amount : 0;
}

export const emptyRecoupData = () => ({ entries: {}, invoices: [] });

// Whatever is stored is treated as untrusted shape: older or hand-edited records must not
// crash the screen, and an unknown stage is dropped rather than guessed at.
export function normaliseRecoupData(formData) {
  const entries = {};
  const rawEntries = formData?.entries && typeof formData.entries === "object" ? formData.entries : {};
  for (const [id, entry] of Object.entries(rawEntries)) {
    if (!id || !RECOUP_STAGE_VALUES.includes(entry?.stage)) continue;
    entries[id] = { ...entry };
  }
  const invoices = (Array.isArray(formData?.invoices) ? formData.invoices : [])
    .filter((invoice) => invoice?.id && RECOUP_STAGE_VALUES.includes(invoice.stage));
  return { entries, invoices };
}

// Every change below returns a new object and never mutates its input. Each is a single
// delta, so that when the server rejects a save because a colleague wrote first, the same
// change can be re-applied to their newer record instead of overwriting their work.

// Puts an invoice already on the screen into a stage, or takes it out with stage = "".
export function setEntryStage(data, row, stage, meta = {}) {
  const entries = { ...data.entries };
  if (!stage) {
    delete entries[row.id];
    return { ...data, entries };
  }
  if (!RECOUP_STAGE_VALUES.includes(stage)) return data;
  const previous = entries[row.id] || {};
  entries[row.id] = {
    ...previous,
    stage,
    clientName: text(row.clientName, 120),
    payee: text(row.counselName && row.counselName !== "Counsel not specified" ? row.counselName : row.clerkName, 120),
    invoiceRef: text(row.invoiceRef || row.invoiceName, 120),
    amount: parseRecoupAmount(row.invoiceAmount),
    source: text(row.source, 40),
    updatedAt: meta.at || new Date().toISOString(),
    updatedBy: text(meta.by, 120),
  };
  return { ...data, entries };
}

export function setEntryNote(data, id, note) {
  if (!data.entries[id]) return data;
  return { ...data, entries: { ...data.entries, [id]: { ...data.entries[id], note: text(note, 500) } } };
}

export function buildRecoupInvoice(fields = {}, meta = {}) {
  return {
    id: meta.id || `recoup-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    stage: RECOUP_STAGE_VALUES.includes(fields.stage) ? fields.stage : "paid_from_budget",
    clientName: text(fields.clientName, 120),
    payee: text(fields.payee, 120),
    invoiceRef: text(fields.invoiceRef, 120),
    invoiceDate: text(fields.invoiceDate, 10),
    paidOn: text(fields.paidOn, 10),
    amount: parseRecoupAmount(fields.amount),
    note: text(fields.note, 500),
    fileName: text(fields.fileName, 200),
    fileUrl: text(fields.fileUrl, 500),
    filePath: text(fields.filePath, 500),
    uploadedAt: meta.at || new Date().toISOString(),
    uploadedBy: text(meta.by, 120),
  };
}

export function addRecoupInvoice(data, invoice) {
  if (data.invoices.some((item) => item.id === invoice.id)) return data;
  return { ...data, invoices: [...data.invoices, invoice] };
}

export function updateRecoupInvoice(data, id, patch = {}) {
  return {
    ...data,
    invoices: data.invoices.map((item) => {
      if (item.id !== id) return item;
      const next = { ...item, ...patch };
      if (!RECOUP_STAGE_VALUES.includes(next.stage)) next.stage = item.stage;
      if ("amount" in patch) next.amount = parseRecoupAmount(patch.amount);
      return next;
    }),
  };
}

// Corrects an uploaded invoice's details. Only what was typed in changes (and the PDF, if a
// replacement is given); its stage stays as it is and it keeps who uploaded it, plus who
// last edited it.
const EDITABLE_INVOICE_FIELDS = {
  clientName: 120, payee: 120, invoiceRef: 120, invoiceDate: 10, paidOn: 10, note: 500,
  fileName: 200, fileUrl: 500, filePath: 500,
};

export function editRecoupInvoice(data, id, fields = {}, meta = {}) {
  const patch = {};
  for (const [key, limit] of Object.entries(EDITABLE_INVOICE_FIELDS)) {
    if (fields[key] !== undefined) patch[key] = text(fields[key], limit);
  }
  if (fields.amount !== undefined) patch.amount = fields.amount;
  patch.editedAt = meta.at || new Date().toISOString();
  patch.editedBy = text(meta.by, 120);
  return updateRecoupInvoice(data, id, patch);
}

export function removeRecoupInvoice(data, id) {
  return { ...data, invoices: data.invoices.filter((item) => item.id !== id) };
}

// One list for the tracker: uploaded invoices plus invoices from the rest of the screen that
// have been given a stage. A tracked row prefers the live row's figures, and falls back to
// the snapshot when that row has not loaded (or has since gone from its source).
export function buildRecoupRows(data, screenRows = []) {
  const live = new Map(screenRows.map((row) => [row.id, row]));
  const tracked = Object.entries(data.entries).map(([id, entry]) => {
    const row = live.get(id);
    return {
      id,
      kind: "tracked",
      stage: entry.stage,
      clientName: row?.clientName || entry.clientName || "Unnamed client",
      payee: entry.payee || row?.counselName || row?.clerkName || "",
      invoiceRef: entry.invoiceRef || row?.invoiceRef || row?.invoiceName || "",
      amount: row ? parseRecoupAmount(row.invoiceAmount) : parseRecoupAmount(entry.amount),
      source: entry.source || row?.source || "",
      note: entry.note || "",
      date: entry.updatedAt || "",
      missing: !row && screenRows.length > 0,
    };
  });
  const uploaded = data.invoices.map((invoice) => ({
    id: invoice.id,
    kind: "uploaded",
    stage: invoice.stage,
    clientName: invoice.clientName || "Unnamed client",
    payee: invoice.payee,
    invoiceRef: invoice.invoiceRef,
    amount: parseRecoupAmount(invoice.amount),
    source: "upload",
    note: invoice.note,
    date: invoice.paidOn || invoice.invoiceDate || invoice.uploadedAt || "",
    invoiceDate: invoice.invoiceDate,
    paidOn: invoice.paidOn,
    fileName: invoice.fileName,
    fileUrl: invoice.fileUrl,
  }));
  return [...uploaded, ...tracked].sort((a, b) =>
    String(a.clientName).localeCompare(String(b.clientName)));
}

export function summariseRecoup(rows = [], prefix = "") {
  const stages = recoupStages(prefix).map((option) => {
    const items = rows.filter((row) => row.stage === option.value);
    return { ...option, items, count: items.length, total: items.reduce((sum, row) => sum + row.amount, 0) };
  });
  const outstanding = stages.filter((stage) => stage.value !== "recovered");
  return {
    stages,
    outstandingCount: outstanding.reduce((sum, stage) => sum + stage.count, 0),
    outstandingTotal: outstanding.reduce((sum, stage) => sum + stage.total, 0),
    recoveredTotal: stages.find((stage) => stage.value === "recovered")?.total || 0,
  };
}

// ---- Reading an invoice PDF's text --------------------------------------------------------
//
// Invoices come from many different senders, so this is best effort: it fills in what it can
// find and the person uploading checks every field before saving. The text never leaves the
// browser - nothing is sent to an outside service to read it.

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const pad = (n) => String(n).padStart(2, "0");

export function toIsoDate(value = "") {
  const raw = String(value).trim();
  let match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) return `${match[1]}-${pad(match[2])}-${pad(match[3])}`;
  match = raw.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (match) {
    const year = match[3].length === 2 ? `20${match[3]}` : match[3];
    const day = Number(match[1]);
    const month = Number(match[2]);
    if (day < 1 || day > 31 || month < 1 || month > 12) return "";
    return `${year}-${pad(month)}-${pad(day)}`;
  }
  match = raw.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,9})\.?,?\s+(\d{4})$/);
  if (match) {
    const month = MONTHS.indexOf(match[2].slice(0, 3).toLowerCase());
    if (month < 0) return "";
    return `${match[3]}-${pad(month + 1)}-${pad(match[1])}`;
  }
  return "";
}

const DATE_PATTERN = "(\\d{4}-\\d{1,2}-\\d{1,2}|\\d{1,2}[/.-]\\d{1,2}[/.-]\\d{2,4}|\\d{1,2}(?:st|nd|rd|th)?\\s+[A-Za-z]{3,9}\\.?,?\\s+\\d{4})";

export function parseInvoiceText(rawText = "") {
  const lines = String(rawText).split(/\r?\n/).map((line) => line.replace(/\s+/g, " ").trim()).filter(Boolean);
  // pdf.js often puts a label and its value on separate lines, so search a flattened copy.
  const flat = lines.join(" \n ");
  const result = { invoiceRef: "", invoiceDate: "", amount: "", payee: "", clientName: "" };

  const ref = flat.match(/\b(?:tax\s+)?invoice\s*(?:no\.?|number|num|#)\s*[:#.]?\s*(?:\n\s*)?([A-Z0-9][A-Z0-9/-]{1,30})/i)
    || flat.match(/\binv(?:oice)?\s*[#:-]\s*([A-Z0-9][A-Z0-9/-]{1,30})/i);
  if (ref && /\d/.test(ref[1])) result.invoiceRef = ref[1];

  const dated = flat.match(new RegExp(`\\b(?:invoice\\s+date|date\\s+of\\s+invoice|date\\s+issued|issue\\s+date|tax\\s+invoice\\s+date)\\s*[:.]?\\s*(?:\\n\\s*)?${DATE_PATTERN}`, "i"))
    || flat.match(new RegExp(`\\bdate\\s*[:.]?\\s*(?:\\n\\s*)?${DATE_PATTERN}`, "i"));
  if (dated) result.invoiceDate = toIsoDate(dated[1]);

  // The same reader the email intake uses for clerks' and report writers' invoices.
  result.amount = readInvoiceAmount(rawText);

  const client = flat.match(/\b(?:client|re|matter|your\s+ref(?:erence)?)\s*[:-]\s*([^\n]{2,60})/i);
  if (client) result.clientName = client[1].replace(/\s+-\s*$/, "").trim();

  // The sender is usually the first real line: skip the document title and bare numbers.
  const payeeLine = lines.find((line) =>
    !/^(tax\s+)?invoice\b/i.test(line)
    && !/^(abn|acn|date|page|to|bill\s+to)\b/i.test(line)
    && /[A-Za-z]{3}/.test(line)
    && line.length <= 80);
  if (payeeLine) result.payee = payeeLine;

  return result;
}
